import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  styled,
  // useTheme, // Removed unused import
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import PaymentMethodSelection from '../components/PaymentMethodSelection';
import StripePayment from '../components/StripePayment';
import PrimaryButton from '../components/styled/PrimaryButton';

const PaymentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '967px',
  margin: '0 auto',
  padding: theme.spacing(6, 0, 4, 0),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  fontFamily: "'Libre Franklin', sans-serif",
}));

// const PaymentSummary = styled(Box)(({ theme }) => ({ // Removed unused component
//   background: theme.palette.mode === 'dark' ? '#1A1A1A' : '#f5f5f5',
//   borderRadius: '12px',
//   padding: theme.spacing(3),
//   marginBottom: theme.spacing(3),
// }));

const PaymentFlow: React.FC = () => {
  // const theme = useTheme(); // Removed unused variable
  const navigate = useNavigate();
  const { loanId } = useParams();
  // const { loan } = useLoanContext();
  // const loan = { name: 'Test Loan' }; // Temporary fallback - removed unused
  
  const [selectedMethod, setSelectedMethod] = useState('stripe');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentDate, setPaymentDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get payment details from URL params or localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const method = params.get('method');
    const amount = params.get('amount');
    const date = params.get('date');
    
    
    if (method) {
      setSelectedMethod(method);
      if (method === 'stripe') {
        setShowPaymentForm(true);
      }
    }
    
    if (amount) {
      const parsedAmount = parseFloat(amount);
      setPaymentAmount(parsedAmount);
    } else {
      // Set a default amount if none provided (for testing)
      setPaymentAmount(422.57); // Default amount for testing
    }
    
    if (date) {
      setPaymentDate(new Date(date));
    }
  }, []);

  const handleMethodSelect = (methodId: string) => {
    setSelectedMethod(methodId);
    setError('');
  };

  const handleContinue = () => {
    if (!selectedMethod) {
      setError('Please select a payment method');
      return;
    }
    
    if (selectedMethod === 'stripe') {
      setShowPaymentForm(true);
    } else {
      // Handle other payment methods
      setError('This payment method is not yet implemented');
    }
  };

  const handlePaymentSuccess = (paymentIntentId: string) => {
    // Show success page first, then it will auto-redirect to repayment history.
    const params = new URLSearchParams({
      amount: paymentAmount.toString(),
      paymentIntentId: paymentIntentId,
      loanId: loanId || '',
      timestamp: new Date().toISOString(),
      method: 'stripe',
      status: 'succeeded'
    });

    navigate(`/payment-success?${params.toString()}`);
  };

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage);
    setLoading(false);
  };

  const handleCancel = () => {
    navigate(`/loans/${loanId}`);
  };

  const handleBack = () => {
    navigate(`/loans/${loanId}/payments`);
  };

  if (!loanId) {
    return (
      <PaymentContainer>
        <Alert severity="error">Loan ID is required</Alert>
      </PaymentContainer>
    );
  }

  return (
    <PaymentContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%', pl: 0.5 }}>
        <Button
          onClick={handleBack}
          sx={{
            minWidth: 'auto',
            p: 0.25,
            color: '#fff',
            fontWeight: 800,
            textTransform: 'none',
            borderRadius: '50%',
            '&:focus-visible': {
              outline: '2px solid rgba(255,255,255,0.85)',
              outlineOffset: '2px',
            },
            '&:active': {
              outline: '2px solid rgba(255,255,255,0.85)',
              outlineOffset: '2px',
            },
          }}
        >
          <ArrowBack sx={{ fontSize: 36 }} />
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}


      {!showPaymentForm ? (
        <>
          <PaymentMethodSelection
            onMethodSelect={handleMethodSelect}
            selectedMethod={selectedMethod}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 6 }}>
            <PrimaryButton onClick={handleCancel}>
              Cancel
            </PrimaryButton>
            <PrimaryButton
              onClick={handleContinue}
              disabled={!selectedMethod}
            >
              Continue
            </PrimaryButton>
          </Box>
        </>
      ) : (
        <StripePayment
          amount={paymentAmount}
          loanId={loanId}
          paymentDate={paymentDate}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onCancel={handleCancel}
        />
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress />
        </Box>
      )}
    </PaymentContainer>
  );
};

export default PaymentFlow; 